import { createSlice } from "@reduxjs/toolkit";
import { AxiosResponse, isAxiosError } from "axios";
import {
  MediaLibrary,
  Picture,
  PictureGroup,
  SvgUploadServerError,
  ThunkDispatch,
} from "../types/index.js";
import { fetch, removeFromArray } from "../utils/utils.js";
import { showAlert } from "./alerts.js";
import { createAppAsyncThunk } from "./utils.js";

const sliceName = "mediaLibrary";

const initialState: MediaLibrary = {
  pictures: {},
  logoIds: [],
  groups: [],
  picturesByGroupId: {},
};

const fetchMedia = async ({
  siteId,
  dispatch,
  groupId,
}: {
  siteId: string;
  dispatch: ThunkDispatch;
  groupId?: number;
}): Promise<Picture[]> => {
  const { data } = await (<Promise<AxiosResponse<Picture[]>>>(
    fetch({ dispatch }).get(`sites/${siteId}/media`, { params: { groupId } })
  ));

  return data;
};

export const getAllMedia = createAppAsyncThunk(
  `${sliceName}/getAllMedia`,
  async (siteId: string, { dispatch }) =>
    await fetchMedia({ siteId, dispatch }),
  {
    condition: (_siteId, { getState }) =>
      getState().loadStates.mediaLibrary.pictures === "unloaded",
  },
);

export const getMediaGroup = createAppAsyncThunk(
  `${sliceName}/getMediaGroup`,
  async (
    { siteId, groupId }: { siteId: string; groupId: number },
    { dispatch },
  ) => await fetchMedia({ siteId, dispatch, groupId }),
  {
    condition: ({ groupId }, { getState }) => {
      const loadStatus =
        getState().loadStates.mediaLibrary.picturesByGroupId[groupId];
      return loadStatus === "unloaded" || loadStatus === undefined;
    },
  },
);

export const getAllMediaGroups = createAppAsyncThunk(
  `${sliceName}/getAllMediaGroups`,
  async (siteId: string, { dispatch }) => {
    try {
      const { data: groups } = await (<Promise<AxiosResponse<PictureGroup[]>>>(
        fetch({ dispatch }).get(`sites/${siteId}/media/groups`)
      ));

      return groups;
    } catch (error) {
      dispatch(
        showAlert("Beim Laden der Bildergruppen ist ein Fehler aufgetreten!"),
      );
      throw error;
    }
  },
  {
    condition: (_siteId, { getState }) =>
      getState().loadStates.mediaLibrary.groups === "unloaded",
  },
);

export const uploadMedia = createAppAsyncThunk(
  `${sliceName}/uploadMedia`,
  async ({ siteId, file }: { siteId: string; file: File }, { dispatch }) => {
    try {
      const { data } = await fetch({ dispatch }).post<Picture>(
        `sites/${siteId}/logos`,
        file,
        { headers: { "Content-Type": "image/svg+xml" } },
      );
      return data;
    } catch (error) {
      let alertMessage = "Beim Hochladen der Datei ist ein Fehler aufgetreten";
      if (isAxiosError<SvgUploadServerError>(error)) {
        const errorCode = error.response?.data?.data?.code;
        if (errorCode === "file_too_large") {
          alertMessage =
            "Die Datei ist zu groß. Bitte wähle eine kleinere Datei aus.";
        } else if (errorCode === "invalid_content_type") {
          alertMessage = "Ungültiger Dateityp. Bitte wähle eine SVG-Datei aus.";
        }
      }
      dispatch(showAlert(alertMessage));
      throw error;
    }
  },
);

export const deleteMedia = createAppAsyncThunk(
  `${sliceName}/deleteMedia`,
  async (
    { siteId, id }: { siteId: string; id: string },
    { dispatch, getState },
  ) => {
    const state = getState();
    const picture = state.mediaLibrary.pictures[id];
    if (!picture) {
      return;
    }

    try {
      const pathParts = picture.url.split("/");
      const filename = pathParts[pathParts.length - 1];

      await fetch({ dispatch }).delete(`sites/${siteId}/logos/${filename}`);
    } catch (error) {
      dispatch(showAlert("Beim Löschen der Datei ist ein Fehler aufgetreten"));
      throw error;
    }
  },
);

const mediaLibrarySlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllMedia.fulfilled, (state, action) => {
      const pictures = action.payload;
      state.pictures = Object.fromEntries(pictures.map((p) => [p.id, p]));
      state.logoIds = pictures
        .filter(({ category }) => category === "logo")
        .map(({ id }) => id);
    });

    builder.addCase(getMediaGroup.fulfilled, (state, action) => {
      const pictures = action.payload;
      const groupId = action.meta.arg.groupId;
      state.picturesByGroupId[groupId] = pictures.map(({ id }) => id);
    });

    builder.addCase(getAllMediaGroups.fulfilled, (state, action) => {
      state.groups = action.payload;
    });

    builder.addCase(uploadMedia.fulfilled, (state, action) => {
      const picture = action.payload;
      state.pictures[picture.id] = picture;
      state.logoIds.push(picture.id);
    });

    builder.addCase(deleteMedia.fulfilled, (state, action) => {
      const id = action.meta.arg.id;
      delete state.pictures[id];
      state.logoIds = removeFromArray(state.logoIds, id);
    });
  },
});

export default mediaLibrarySlice.reducer;
